import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import { useBreakpoint } from 'use-breakpoint';

import MedicalRecordsClinicRequest from './MedicalRecordsClinicRequest';
import { CustomerPatientRecordsAvailabilityEnum } from '../__generated__/graphql';
import CheckBoxButton from '../components/CheckBoxButton';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import useWebBookingStore from '../store';

type Props = {
    goToNextStep: ()=> void;
};

const MedicalRecords = ({ goToNextStep }: Props) => {
    const { patient, updatePatient, updateOnGoToPrevStep } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
        updateOnGoToPrevStep: state.setOnGoToPrevStep
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';
    const [recordsAvailability, setRecordsAvailability] = useState<CustomerPatientRecordsAvailabilityEnum | null>(null);

    const [activeStep, setActiveStep] = useState<'initial' | 'externalRecordsRequest'>('initial');

    useEffect(() => {
        setRecordsAvailability(patient.recordsAvailability);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const next = (skip?: boolean) => {
        updatePatient({
            ...patient,
            recordsAvailability: skip ? CustomerPatientRecordsAvailabilityEnum.NoRecords : recordsAvailability,
        });

        goToNextStep();
    };

    const renderChoices = () => {
        const choices = [
            {
                label: 'My pet has records from a previous veterinary clinic',
                value: CustomerPatientRecordsAvailabilityEnum.RecordsNeeded,
                onClick: () => {
                    updateOnGoToPrevStep(() => {
                        setRecordsAvailability(null);
                        setActiveStep('initial');
                    });
                    setActiveStep('externalRecordsRequest');
                }
            },
            {
                label: 'My pet has records but not from a clinic (i.e. a breeder or rescue)',
                value: CustomerPatientRecordsAvailabilityEnum.PendingClient,
                onClick: () => setRecordsAvailability(CustomerPatientRecordsAvailabilityEnum.PendingClient)
            },
            {
                label: 'My pet does not have any medical history',
                value: CustomerPatientRecordsAvailabilityEnum.NoRecords,
                onClick: () => setRecordsAvailability(CustomerPatientRecordsAvailabilityEnum.NoRecords)
            }
        ];

        return (
            <Subbody>
                {choices.map(({ label, value, onClick }) => (
                    <CheckBoxButton
                        key={value}
                        label={label}
                        checked={recordsAvailability === value}
                        onClick={onClick}
                    />
                ))}
            </Subbody>
        );
    };

    const renderExternalRecordsRequest = () => (
        <MedicalRecordsClinicRequest goToNextStep={goToNextStep} />
    );

    const renderInitialStep = () => (
        <>
            <Body>
                <Instructions>
                    <Fonts.Heading04>Any previous medical records?</Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        To ensure we can offer the most comprehensive care, please share any
                        previous medical records with us.
                    </Fonts.Body>
                </Instructions>
                {renderChoices()}
            </Body>
            <FooterButtons isMobile={isMobile}>
                <PrimaryButton
                    id="medical-records-button"
                    disabled={!recordsAvailability}
                    color="periwinkle"
                    onClick={() => next()}
                >
                    Next
                </PrimaryButton>
                <PrimaryButton
                    id="request-later-button"
                    color="white"
                    onClick={() => {
                        next(true);
                    }}
                >
                    Request Later
                </PrimaryButton>
            </FooterButtons>
        </>
    );

    return (
        <MedicalRecordsContainer>
            {activeStep === 'initial'
                ? renderInitialStep()
                : renderExternalRecordsRequest()}
        </MedicalRecordsContainer>
    );
};

export default MedicalRecords;

const MedicalRecordsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 32px 24px 0 24px;
  gap: 8px;
  justify-content: space-between;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FooterButtons = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
  bottom: 0px;
  background-color: ${colors.WHITE};
  padding: 16px 0 32px 0;
`;

const Instructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Subbody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
